import * as React from 'react';
import type { Session, SupabaseClient, User, UserIdentity } from '@supabase/supabase-js';
import { isGoTrueFragment, isLinkLandingPath, linkFailureCode, maskEmail, readAuthLink } from '@/lib/authLink';
import { describeAuthError, describeOAuthError, type AuthErrorKey } from '@/lib/authErrors';
import { clearOAuthPending, safeAppPath, takeOAuthPending, writeOAuthPending, type OAuthProvider } from '@/lib/oauthPending';

/** A sign-in link that did not sign anyone in: `otp_expired` and friends, `invalid_link`, `other_browser`
 *  (asked for in another browser), `other_account` (another account's link while one is signed in: `email`
 *  is that account's address, masked). `via: 'oauth'` when this tab had sent the person to Google or Apple
 *  (the marker, see lib/oauthPending): the page then reads it with `oauthErrorKey`, not `linkErrorKey`.
 *  `error` is GoTrue's raw `error` param beside `code` (its `error_code`). */
export interface LinkError {
  code: string;
  email?: string;
  via?: 'link' | 'oauth';
  purpose?: 'signin' | 'link';
  error?: string;
}

/** This page load signed `email` in from a return: a sign-in link (`via: 'link'`) or Google / Apple
 *  (`via: 'oauth'`). `purpose: 'link'` is a linkIdentity round trip; `from` is where a sign-in was headed. */
export interface FreshLink {
  email: string;
  via: 'link' | 'oauth';
  purpose: 'signin' | 'link';
  from: string | null;
  /** The provider of an OAuth return (null for a sign-in link). */
  provider: OAuthProvider | null;
}

interface AuthContextValue {
  client: SupabaseClient;
  session: Session | null;
  user: User | null;
  /** True until GoTrue has answered whether a stored session is still valid (and a link's code is exchanged). */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  /** The session ended by this tab's own Sign out (not an expiry): the guard then forgets the page the
   *  person was on, so whoever signs in next lands on the dashboard, not on the last user's page (F14). */
  signedOut: boolean;
  /** A sign-in link that failed: the page says why and offers to send a new one. */
  linkError: LinkError | null;
  clearLinkError: () => void;
  /** This page load signed `email` in from a return: a link's page confirms who that is before going on. */
  freshLink: FreshLink | null;
  clearFreshLink: () => void;
  /** signInWithOtp. `redirectPath` is a landing path ('/invite/<id>', '/signin'); anything else is refused. */
  sendSignInLink: (email: string, redirectPath: string, opts: { createUser: boolean }) => Promise<{ error: AuthErrorKey | null }>;
  /** signInWithOAuth to Google or Apple, back to a landing path; `from` is kept for the return. */
  signInWithProvider: (provider: OAuthProvider, redirectPath: string, opts?: { from?: string | null }) => Promise<{ error: AuthErrorKey | null }>;
  /** linkIdentity (manual linking) for the signed-in user, back to /settings/profile. */
  linkProvider: (provider: OAuthProvider) => Promise<{ error: AuthErrorKey | null }>;
  /** unlinkIdentity, then a session refresh (the JWT's identities change). Never throws. */
  unlinkProvider: (identity: UserIdentity) => Promise<{ error: AuthErrorKey | null }>;
  /** auth.updateUser({ password }): anyone who joined by sign-in link sets one for next time. */
  setPassword: (password: string) => Promise<{ error: AuthErrorKey | null }>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

interface LinkOutcome {
  error: LinkError | null;
  fresh: FreshLink | null;
}

/** Where a linkIdentity round trip comes back (a return path, never a sign-in landing path). */
const LINK_RETURN_PATH = '/settings/profile';

/** Strip GoTrue's parameters from the address bar (replaceState: they never linger in history either). */
function replaceUrl(cleanUrl: string | null) {
  if (cleanUrl !== null) window.history.replaceState(window.history.state, '', cleanUrl);
}

/**
 * Complete a sign-in link, once per page load (design section 2, F1). The URL is cleaned BEFORE any
 * await. Only `?code=` on a landing path signs anyone in, through the PKCE exchange (which needs the
 * verifier this browser stored when it asked for the link); implicit-flow tokens are dropped unread.
 *
 * A live session and a code for ANOTHER account: auth-js has already replaced the session, so the new
 * one is signed out (scope local: only that session is revoked) and the live one put back. Nothing
 * switches accounts silently; the page says whose link it was, masked.
 *
 * The OAuth marker is taken on every load (a stale one never outlives the page load that could use it):
 * with it the return came from Google or Apple, without it from a sign-in link.
 */
async function consumeLink(client: SupabaseClient): Promise<LinkOutcome> {
  const { link, cleanUrl } = readAuthLink(window.location);
  const pending = takeOAuthPending(window.location.pathname);
  replaceUrl(cleanUrl);
  const via = pending ? 'oauth' : 'link';
  const purpose = pending?.purpose ?? 'signin';
  if (link.kind === 'error') {
    console.warn('sign-in return refused', via, link.code, link.description);
    return { error: { code: link.code, error: link.error, via, purpose }, fresh: null };
  }
  if (link.kind !== 'code') return { error: null, fresh: null };

  const { data: before } = await client.auth.getSession();
  const live = before.session;
  const { data, error } = await client.auth.exchangeCodeForSession(link.code, link.flowId ? { flowId: link.flowId } : undefined);
  if (error || !data.session) {
    console.warn('sign-in link code refused', error);
    return { error: { code: error ? linkFailureCode(error) : 'invalid_link', via, purpose }, fresh: null };
  }
  const next = data.session;
  if (live && live.user.id !== next.user.id) {
    console.warn('sign-in link for another account ignored');
    await client.auth.signOut({ scope: 'local' });
    const { error: restoreError } = await client.auth.setSession({
      access_token: live.access_token,
      refresh_token: live.refresh_token,
    });
    if (restoreError) console.warn('live session not restored', restoreError);
    return { error: { code: 'other_account', email: maskEmail(next.user.email), via, purpose }, fresh: null };
  }
  return { error: null, fresh: { email: next.user.email ?? '', via, purpose, from: pending?.from ?? null, provider: pending?.provider ?? null } };
}

interface Boot {
  client: SupabaseClient;
  done: Promise<LinkOutcome>;
  settled: boolean;
}

function startBoot(client: SupabaseClient): Boot {
  const boot: Boot = { client, done: Promise.resolve({ error: null, fresh: null }), settled: false };
  boot.done = consumeLink(client).finally(() => {
    boot.settled = true;
  });
  return boot;
}

export function AuthProvider({
  client,
  children,
}: {
  client: SupabaseClient;
  children: React.ReactNode;
}) {
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [linkError, setLinkError] = React.useState<LinkError | null>(null);
  const [freshLink, setFreshLink] = React.useState<FreshLink | null>(null);
  // StrictMode runs the effect twice on the same instance: the link is consumed by the first run and
  // both runs wait on the same promise (a second exchange of the same code would fail). Until it
  // settles, auth events are not rendered: an exchange for another account fires SIGNED_IN with that
  // account before it is undone, and not even one render may show it.
  const boot = React.useRef<Boot | null>(null);

  React.useEffect(() => {
    let active = true;
    if (!boot.current || boot.current.client !== client) boot.current = startBoot(client);
    const mine = boot.current;

    // Two paths reach the same state: the stored session read at boot, and every later auth event.
    // getSession() resolves from storage without a round trip, so the guard never flashes the sign-in
    // form at an already-authenticated dispatcher.
    void mine.done.then(async (outcome) => {
      if (!active) return;
      if (outcome.error) setLinkError(outcome.error);
      if (outcome.fresh) setFreshLink(outcome.fresh);
      const { data } = await client.auth.getSession();
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: subscription } = client.auth.onAuthStateChange((event, next) => {
      if (!active || !mine.settled) return;
      setSession(next);
      if (event === 'SIGNED_OUT') setFreshLink(null);
      if (next) setLoading(false);
    });

    // A link pasted into the tab already on the page changes only the hash when it carries implicit-flow
    // tokens: those are never applied, only removed from the address bar. (A `?code=` link is a real
    // navigation, so the boot above sees it.) A plain #section is left alone.
    const onHashChange = () => {
      if (!isGoTrueFragment(window.location.hash)) return;
      replaceUrl(window.location.pathname + window.location.search);
    };
    window.addEventListener('hashchange', onHashChange);

    return () => {
      active = false;
      window.removeEventListener('hashchange', onHashChange);
      subscription.subscription.unsubscribe();
    };
  }, [client]);

  const signIn = React.useCallback<AuthContextValue['signIn']>(
    async (email, password) => {
      const { error } = await client.auth.signInWithPassword({ email, password });
      return { error: error ? error.message : null };
    },
    [client],
  );

  const [signedOut, setSignedOut] = React.useState(false);
  React.useEffect(() => {
    if (session) setSignedOut(false);
  }, [session]);

  const signOut = React.useCallback(async () => {
    setSignedOut(true);
    // Local scope: signing out of this browser never ends the user's sessions on other devices.
    await client.auth.signOut({ scope: 'local' });
  }, [client]);

  const clearLinkError = React.useCallback(() => setLinkError(null), []);
  const clearFreshLink = React.useCallback(() => setFreshLink(null), []);

  const sendSignInLink = React.useCallback<AuthContextValue['sendSignInLink']>(
    async (email, redirectPath, { createUser }) => {
      // The link comes back to this origin and one of the two landing paths, nowhere else: a code that
      // lands anywhere else is never exchanged, so a link sent elsewhere would only be a dead link.
      if (!isLinkLandingPath(redirectPath)) {
        console.warn('sign-in link redirect refused', redirectPath);
        return { error: 'generic' };
      }
      const { error } = await client.auth.signInWithOtp({
        email: email.trim().toLowerCase(),
        options: { emailRedirectTo: window.location.origin + redirectPath, shouldCreateUser: createUser },
      });
      if (!error) return { error: null };
      console.warn('sign-in link not sent', error);
      return { error: describeAuthError(error, { enumerationSafe: !createUser }) };
    },
    [client],
  );

  const signInWithProvider = React.useCallback<AuthContextValue['signInWithProvider']>(
    async (provider, redirectPath, opts) => {
      // Same door as a sign-in link: the code comes back to one of the two landing paths or nowhere.
      if (!isLinkLandingPath(redirectPath)) {
        console.warn('oauth redirect refused', redirectPath);
        return { error: 'generic' };
      }
      writeOAuthPending({ purpose: 'signin', provider, path: redirectPath, from: safeAppPath(opts?.from) });
      const { error } = await client.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin + redirectPath,
          // Google otherwise signs in with whichever account the browser last used: the chooser is the
          // "is this you?" step a sign-in link gets from its confirm screen.
          queryParams: provider === 'google' ? { prompt: 'select_account' } : undefined,
        },
      });
      if (!error) return { error: null };
      clearOAuthPending();
      console.warn('oauth sign-in not started', error);
      return { error: describeOAuthError(error) };
    },
    [client],
  );

  const linkProvider = React.useCallback<AuthContextValue['linkProvider']>(
    async (provider) => {
      writeOAuthPending({ purpose: 'link', provider, path: LINK_RETURN_PATH, from: null });
      const { error } = await client.auth.linkIdentity({ provider, options: { redirectTo: window.location.origin + LINK_RETURN_PATH } });
      if (!error) return { error: null };
      clearOAuthPending();
      console.warn('identity link not started', error);
      return { error: describeOAuthError(error) };
    },
    [client],
  );

  const unlinkProvider = React.useCallback<AuthContextValue['unlinkProvider']>(
    async (identity) => {
      try {
        const { error } = await client.auth.unlinkIdentity(identity);
        if (error) {
          console.warn('identity not unlinked', error);
          return { error: describeOAuthError(error) };
        }
        // unlinkIdentity leaves the session as it was: refresh it so the JWT stops listing the identity.
        const { error: refreshError } = await client.auth.refreshSession();
        if (refreshError) console.warn('session not refreshed after unlink', refreshError);
        return { error: null };
      } catch (err) {
        console.warn('identity not unlinked', err);
        return { error: 'oauthFailed' };
      }
    },
    [client],
  );

  const setPassword = React.useCallback<AuthContextValue['setPassword']>(
    async (password) => {
      const { error } = await client.auth.updateUser({ password });
      if (!error) return { error: null };
      console.warn('password not set', error);
      return { error: describeAuthError(error) ?? 'generic' };
    },
    [client],
  );

  const value = React.useMemo<AuthContextValue>(
    () => ({
      client,
      session,
      user: session?.user ?? null,
      loading,
      signIn,
      signOut,
      signedOut,
      linkError,
      clearLinkError,
      freshLink,
      clearFreshLink,
      sendSignInLink,
      signInWithProvider,
      linkProvider,
      unlinkProvider,
      setPassword,
    }),
    [
      client,
      session,
      loading,
      signIn,
      signOut,
      signedOut,
      linkError,
      clearLinkError,
      freshLink,
      clearFreshLink,
      sendSignInLink,
      signInWithProvider,
      linkProvider,
      unlinkProvider,
      setPassword,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
